import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Key, Lock, Unlock, Copy, Trash2, ArrowLeftRight, Upload, Download } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

const Base64Encoder: React.FC = () => {
  const [input, setInput] = useState('');
  const [encodedOutput, setEncodedOutput] = useState('');
  const [decodedOutput, setDecodedOutput] = useState('');
  const [activeTab, setActiveTab] = useState<'encode' | 'decode'>('encode');
  const [inputType, setInputType] = useState<'text' | 'file'>('text');

  useSEO({
    title: 'Base64 Encoder & Decoder - Encode and Decode Base64 Online',
    description: 'Free online Base64 encoder and decoder. Convert text and files to Base64 encoding and decode Base64 strings back to original format.',
    keywords: 'Base64 encoder, Base64 decoder, encode Base64, decode Base64, Base64 converter, file to Base64',
    canonical: 'https://snap-tools.online/dev/base64-encoder'
  });

  const handleInputChange = (value: string) => {
    setInput(value);
    if (value.trim()) {
      if (activeTab === 'encode') {
        try {
          const encoded = btoa(unescape(encodeURIComponent(value)));
          setEncodedOutput(encoded);
        } catch (error) {
          setEncodedOutput('Error encoding text');
        }
      } else {
        try {
          const decoded = decodeURIComponent(escape(atob(value)));
          setDecodedOutput(decoded);
        } catch (error) {
          setDecodedOutput('Invalid Base64 format');
        }
      }
    } else {
      setEncodedOutput('');
      setDecodedOutput('');
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      if (activeTab === 'encode') {
        const base64 = result.split(',')[1]; // Remove data:type;base64, prefix
        setEncodedOutput(base64);
        setInput(`[File: ${file.name}]`);
        toast.success('File encoded to Base64');
      }
    };
    reader.readAsDataURL(file);
  };

  const copyOutput = () => {
    const output = activeTab === 'encode' ? encodedOutput : decodedOutput;
    navigator.clipboard.writeText(output).then(() => {
      toast.success('Output copied to clipboard');
    });
  };

  const downloadOutput = () => {
    const output = activeTab === 'encode' ? encodedOutput : decodedOutput;
    const filename = activeTab === 'encode' ? 'encoded.txt' : 'decoded.txt';
    
    const blob = new Blob([output], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded`);
  };

  const clearInputs = () => {
    setInput('');
    setEncodedOutput('');
    setDecodedOutput('');
    toast.success('All fields cleared');
  };

  const loadSample = () => {
    if (activeTab === 'encode') {
      setInput('Hello World! This is a sample text for Base64 encoding.');
    } else {
      setInput('SGVsbG8gV29ybGQhIFRoaXMgaXMgYSBzYW1wbGUgdGV4dCBmb3IgQmFzZTY0IGVuY29kaW5nLg==');
    }
    toast.success('Sample data loaded');
  };

  const switchMode = () => {
    const newTab = activeTab === 'encode' ? 'decode' : 'encode';
    setActiveTab(newTab);
    setInputType('text');
    
    // Auto-fill input with current output for reverse operation
    if (activeTab === 'encode' && encodedOutput && !input.includes('[File:')) {
      setInput(encodedOutput);
    } else if (activeTab === 'decode' && decodedOutput) {
      setInput(decodedOutput);
    }
  };

  const formatBase64 = (text: string) => {
    // Add line breaks every 76 characters for better readability
    return text.replace(/.{76}/g, '$&\n');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <Key className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            Base64 Encoder & Decoder
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Encode text and files to Base64 format or decode Base64 strings back to original format. 
            Support for UTF-8 text and binary files up to 5MB.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        {/* Tab Navigation */}
        <div className="flex justify-center mb-6 sm:mb-8">
          <div className="bg-white rounded-xl p-1 shadow-lg border border-gray-100">
            <button
              onClick={() => setActiveTab('encode')}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base flex items-center space-x-2 ${
                activeTab === 'encode'
                  ? 'bg-[#8438ff] text-white shadow-md'
                  : 'text-gray-600 hover:text-[#8438ff] hover:bg-gray-50'
              }`}
            >
              <Lock className="h-4 w-4" />
              <span>Encode to Base64</span>
            </button>
            <button
              onClick={() => setActiveTab('decode')}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base flex items-center space-x-2 ${
                activeTab === 'decode'
                  ? 'bg-[#8438ff] text-white shadow-md'
                  : 'text-gray-600 hover:text-[#8438ff] hover:bg-gray-50'
              }`}
            >
              <Unlock className="h-4 w-4" />
              <span>Decode from Base64</span>
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Input Section */}
          <div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6 lg:p-8 hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
                  {activeTab === 'encode' ? (
                    <>
                      <Upload className="h-5 w-5 mr-2 text-[#8438ff]" />
                      Input {inputType === 'file' ? 'File' : 'Text'}
                    </>
                  ) : (
                    <>
                      <Key className="h-5 w-5 mr-2 text-[#8438ff]" />
                      Input Base64
                    </>
                  )}
                </h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={loadSample}
                    className="px-3 py-1.5 text-xs sm:text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 font-medium shadow-sm"
                  >
                    Load Sample
                  </button>
                  <button
                    onClick={switchMode}
                    className="p-1.5 sm:p-2 text-gray-500 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-all duration-200"
                    title="Switch mode"
                  >
                    <ArrowLeftRight className="h-4 w-4" />
                  </button>
                  <button
                    onClick={clearInputs}
                    className="p-1.5 sm:p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200"
                    title="Clear all"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Input Type Toggle for Encode */}
              {activeTab === 'encode' && (
                <div className="mb-4">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setInputType('text')}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        inputType === 'text'
                          ? 'bg-[#8438ff] text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Text Input
                    </button>
                    <button
                      onClick={() => setInputType('file')}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        inputType === 'file'
                          ? 'bg-[#8438ff] text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      File Upload
                    </button>
                  </div>
                </div>
              )}

              {/* File Upload */}
              {activeTab === 'encode' && inputType === 'file' ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">Upload a file to encode to Base64</p>
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#8438ff] file:text-white hover:file:bg-[#7228ee]"
                  />
                  <p className="text-xs text-gray-500 mt-2">Maximum file size: 5MB</p>
                </div>
              ) : (
                <textarea
                  value={input}
                  onChange={(e) => handleInputChange(e.target.value)}
                  placeholder={
                    activeTab === 'encode'
                      ? 'Enter text to encode to Base64...'
                      : 'Enter Base64 string to decode...'
                  }
                  className="w-full h-40 sm:h-48 p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl resize-none focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors font-mono text-xs sm:text-sm"
                />
              )}

              <div className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-600">
                <p className="mb-2">
                  <strong>{activeTab === 'encode' ? 'Encoding' : 'Decoding'}:</strong> 
                  {activeTab === 'encode' 
                    ? ' Converts text/files to Base64 format for safe transmission'
                    : ' Converts Base64 strings back to original text'
                  }
                </p>
                <p>Input length: {input.length} characters</p>
              </div>
            </motion.div>
          </div>

          {/* Output Section */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 lg:p-8"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  {activeTab === 'encode' ? 'Base64 Encoded' : 'Decoded Text'}
                </h3>
                <div className="flex space-x-1 sm:space-x-2">
                  {(activeTab === 'encode' ? encodedOutput : decodedOutput) && (
                    <>
                      <button
                        onClick={copyOutput}
                        className="p-1.5 sm:p-2 text-gray-500 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-colors"
                        title="Copy to clipboard"
                      >
                        <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </button>
                      <button
                        onClick={downloadOutput}
                        className="p-1.5 sm:p-2 text-gray-500 hover:text-green-500 hover:bg-green-50 rounded-lg transition-colors"
                        title="Download as file"
                      >
                        <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              <textarea
                value={
                  activeTab === 'encode' 
                    ? (encodedOutput.length > 1000 ? formatBase64(encodedOutput) : encodedOutput)
                    : decodedOutput
                }
                readOnly
                className="w-full h-40 sm:h-48 p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl resize-none bg-gray-50 font-mono text-xs sm:text-sm"
                placeholder={`${activeTab === 'encode' ? 'Base64 encoded' : 'Decoded'} output will appear here...`}
              />

              {(activeTab === 'encode' ? encodedOutput : decodedOutput) && (
                <div className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-600">
                  <p>Output length: {(activeTab === 'encode' ? encodedOutput : decodedOutput).length} characters</p>
                  {activeTab === 'encode' && (
                    <p>Size increase: {(((encodedOutput.length / Math.max(input.length, 1)) - 1) * 100).toFixed(1)}%</p>
                  )}
                </div>
              )}

              {/* Base64 Info */}
              <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Base64 Information</h4>
                <div className="text-xs text-blue-800 space-y-1">
                  <p>• Uses characters: A-Z, a-z, 0-9, +, /</p>
                  <p>• Padding character: = (for alignment)</p>
                  <p>• Increases size by ~33% for text</p>
                  <p>• Safe for transmission over text-based protocols</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8"
        />
      </div>
    </div>
  );
};

export default Base64Encoder;
